import { AgentLog, AgentPrompt, AgentTestInput, AgentTestResult } from '@/types/agent-types';
import { API_BASE_URL as CONFIG_API_BASE_URL } from '@/config/api';
import { authFetch } from '@/utils/auth-fetch';
import { AuthFetchError } from '@/utils/auth-fetch-error';
import { describeResponseError } from '@/utils/responseError';
import { INPUT_SHAPE_TABLE, firstRecorded } from '@/lib/agent-log-shapes';

/**
 * An AgentLog plus the row columns the log routes can carry, shaped by normalizeAgentLogRow.
 * duration_ms is the one duration field (milliseconds from whichever column recorded it).
 */
export type NormalizedAgentLogRow = AgentLog & {
  duration_ms?: number;
  status?: string | null;
  error?: string | null;
  trace_id?: string;
  id?: string;
};

// Use the base URL from config (without /api suffix since we explicitly include it in each endpoint)
const API_BASE_URL = CONFIG_API_BASE_URL;

// API URL constants for consistent endpoints
const API_ENDPOINTS = {
  AGENT_TEST: `${API_BASE_URL}/agents/test-run`,
  // Updated to use the correct prompts endpoint (plural)
  AGENT_PROMPTS: (agentType: string) => `${API_BASE_URL}/agents/${agentType}/prompts`,
  AGENT_BY_ID: (agentId: string) => `${API_BASE_URL}/agents/${agentId}`,
  // Agent logs endpoint - the server.js endpoint expects query param format
  AGENT_LOGS: (agentType: string) => `${API_BASE_URL}/agents/logs`,
  // Debug endpoints - for direct access in case of middleware issues
  DEBUG_LOGS: `${API_BASE_URL}/debug/logs`,
  DEBUG_AGENT_LOGS: (agentType: string) => `${API_BASE_URL}/debug/agent-logs/${agentType}`
};

// For debugging - log API requests
const debugMode = true; // Set to false in production
const debugLog = (message: string, data?: any) => {
  if (debugMode) {
    console.log(`[AgentAPI] ${message}`, data || '');
  }
};

/**
 * API service for agent-related operations with fixed endpoints
 */
export const agentApiFixed = {
  /**
   * Fetch all available agent types from the database
   */
  async getAgentTypes(): Promise<string[]> {
    debugLog('Fetching agent types from database');
    try {
      // Try to fetch from database API endpoint
      const response = await authFetch(`${API_BASE_URL}/agents/types`);

      if (response.ok) {
        const data = await response.json();
        debugLog('Successfully fetched agent types from database', data);

        if (Array.isArray(data) && data.length > 0) {
          return data;
        }
      }

      const typesDiagnostic = response.ok
        ? 'No agent types returned by /agents/types'
        : await describeResponseError(response, 'Failed to fetch /agents/types');
      debugLog(`${typesDiagnostic}; trying the agents collection`);

      // Preserve the existing lookup of real types from the agents collection.
      const agentsResponse = await authFetch(`${API_BASE_URL}/agents`);

      if (agentsResponse.ok) {
        const agentsData = await agentsResponse.json();
        if (agentsData && agentsData.agents && Array.isArray(agentsData.agents)) {
          // Extract unique agent_type values from the agents collection
          const agentTypes = new Set<string>();

          agentsData.agents.forEach((agent: any) => {
            if (agent.agent_type) {
              agentTypes.add(agent.agent_type);
            }
          });

          if (agentTypes.size > 0) {
            const types = Array.from(agentTypes);
            debugLog('Extracted agent types from agents collection', types);
            return types;
          }
        }
      }

      const agentsDiagnostic = agentsResponse.ok
        ? 'No agent types returned by /agents'
        : await describeResponseError(agentsResponse, 'Failed to fetch /agents');
      throw new Error(`${typesDiagnostic}; ${agentsDiagnostic}`);
    } catch (error) {
      console.error('Error fetching agent types:', error);
      if (error instanceof AuthFetchError) {
        throw new Error(await describeResponseError(error.response, error.message));
      }
      throw error;
    }
  },

  /**
   * Get the latest prompt for a specific agent type or ID
   */
  async getLatestPrompt(agentId: string): Promise<AgentPrompt> {
    let agentType = agentId.trim();
    if (!agentType) throw new Error('An agent type or ID is required to load a prompt');

    const fetchPromptResponse = async (url: string) => {
      try {
        return await authFetch(url);
      } catch (error) {
        // authFetch throws on 401 after starting the login redirect.
        // Preserve its HTTP diagnostic as well as ordinary returned failures.
        if (error instanceof AuthFetchError) {
          throw new Error(await describeResponseError(error.response, error.message));
        }
        throw error;
      }
    };

    // Legacy MongoDB IDs must resolve to a real agent; never guess its type.
    if (/^[0-9a-f]{24}$/i.test(agentType)) {
      const agentResponse = await fetchPromptResponse(API_ENDPOINTS.AGENT_BY_ID(encodeURIComponent(agentType)));
      if (!agentResponse.ok) {
        throw new Error(await describeResponseError(agentResponse, `Failed to resolve agent ${agentType}`));
      }
      const agentData = await agentResponse.json();
      if (typeof agentData?.agent?.agent_type !== 'string' || !agentData.agent.agent_type.trim()) {
        throw new Error(`Invalid agent response for ${agentType}: missing agent.agent_type`);
      }
      agentType = agentData.agent.agent_type.trim();
    }

    const response = await fetchPromptResponse(API_ENDPOINTS.AGENT_PROMPTS(encodeURIComponent(agentType)));
    if (!response.ok) {
      throw new Error(await describeResponseError(response, `Failed to load prompt for ${agentType}`));
    }
    const data = await response.json();
    if (!Array.isArray(data?.prompts)) {
      throw new Error(`Invalid prompt response for ${agentType}: expected a prompts array`);
    }
    if (data.prompts.length === 0) {
      throw new Error(`No saved prompt found for ${agentType}`);
    }

    // Every timestamp is needed to establish which saved prompt is newest.
    for (const [index, prompt] of data.prompts.entries()) {
      if (
        !['string', 'number'].includes(typeof prompt?.created_at) ||
        !Number.isFinite(new Date(prompt.created_at).getTime())
      ) {
        throw new Error(`Invalid prompt response for ${agentType}: prompts[${index}].created_at must be a valid timestamp`);
      }
    }

    const latestPrompt = [...data.prompts].sort(
      (a: AgentPrompt, b: AgentPrompt) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];
    // Validate the current version, so a later valid save can supersede blank history.
    // Never replace an invalid current version with older text.
    if (typeof latestPrompt.prompt !== 'string' || !latestPrompt.prompt.trim()) {
      throw new Error(`Invalid prompt response for ${agentType}: newest prompt must be non-empty text`);
    }
    return latestPrompt;
  },

  /**
   * Save a new version of a prompt for an agent type
   */
  async savePrompt(agentId: string, prompt: string): Promise<AgentPrompt> {
    debugLog(`Saving prompt for ${agentId}`);

    try {
      // Convert MongoDB ID to agent type if needed
      let agentTypeId = agentId;
      if (/^[0-9a-f]{24}$/i.test(agentId)) {
        try {
          const agentResponse = await authFetch(API_ENDPOINTS.AGENT_BY_ID(agentId));
          if (agentResponse.ok) {
            const agentData = await agentResponse.json();
            if (agentData && agentData.agent && agentData.agent.agent_type) {
              agentTypeId = agentData.agent.agent_type;
              debugLog(`Converted agent ID to type: ${agentTypeId}`);
            }
          } else {
            debugLog(`Failed to fetch agent with ID ${agentId}, response status: ${agentResponse.status}`);
            // Use a default type since we can't determine the actual type
            agentTypeId = 'page-role';
            debugLog(`Using default agent type: ${agentTypeId}`);
          }
        } catch (error) {
          debugLog(`Error converting agent ID: ${error}`);
          // Continue with original ID if conversion fails
        }
      }
      
      // Use the API endpoint to save the prompt - matches the endpoint patterns from our tests
      const response = await authFetch(`${API_BASE_URL}/agents/${agentTypeId}/prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_type: agentTypeId,
          prompt,
          version: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        const result = await response.json();
        debugLog(`Successfully saved prompt for ${agentId}`);
        return result;
      }

      // If the API fails, throw an error instead of returning mock data
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`Failed to save prompt: ${response.status} ${errorText}`);
    } catch (error) {
      console.error('Error saving prompt:', error);
      // Re-throw the error instead of returning mock data
      throw error;
    }
  },

  /**
   * Get all prompt versions for a specific agent type
   */
  async getPromptHistory(agentType: string): Promise<AgentPrompt[]> {
    debugLog(`Getting prompt history for ${agentType} (mock implementation)`);
    
    // Mock implementation
    return [
      {
        _id: 'mock-id-1',
        agent_type: agentType,
        prompt: 'Previous version of the prompt...',
        version: '1.0.0',
        created_at: new Date(Date.now() - 86400000) // yesterday
      },
      {
        _id: 'mock-id-2',
        agent_type: agentType,
        prompt: 'Current version of the prompt...',
        version: '1.1.0',
        created_at: new Date()
      }
    ];
  },

  /**
   * Get logs for an agent with optional filters
   */
  /**
   * Get logs for an agent with optional filters
   * OPTIMIZED VERSION - selects best endpoint without trying multiple in sequence
   */
  /**
   * GET /api/logs/by-agent-type/:agentType. Requires an agent type (the previous
   * silent 'page-role' default queried another agent's logs and reported them as
   * this one's) and THROWS on a non-ok response or a body without a logs array.
   * An empty list is only ever the API's own answer.
   */
  async getLogs(params: {
    agentType?: string;
    traceId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ logs: NormalizedAgentLogRow[]; total: number }> {
    if (!params.agentType) throw new Error('getLogs: an agent type is required');
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.traceId) queryParams.append('trace_id', params.traceId);
    const qs = queryParams.toString();
    const url = `${API_BASE_URL}/api/logs/by-agent-type/${encodeURIComponent(params.agentType)}${qs ? `?${qs}` : ''}`;
    debugLog(`Fetching logs from ${url}`);
    const response = await authFetch(url);
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`GET ${url} returned HTTP ${response.status}${body ? `: ${body.slice(0, 300)}` : ''}`);
    }
    const data = await response.json();
    if (!data || !Array.isArray(data.logs)) throw new Error(`GET ${url} returned no logs array in its body`);
    // The route answers { logs, pagination: { total, offset, limit } } (routes/logs-postgresql.js
    // by-agent-type handler). The total is the route's count, never the page length.
    const total = data.pagination?.total;
    if (typeof total !== 'number') throw new Error(`GET ${url} returned no pagination.total in its body`);
    // Raw agent_logs rows (SELECT *, snake_case); one shape for every reader.
    return { logs: data.logs.map((row: Record<string, any>) => agentApiFixed.normalizeAgentLogRow(row)), total };
  },

  /**
   * GET /api/logs/by-trace/:traceId with the auth token. 200 returns the row through
   * normalizeAgentLogRow (the only path from a row to an AgentLog); 404 is
   * the route's own answer that no row exists yet (a test still running) and returns
   * null; anything else THROWS with status and body. The by-agent-type route ignores
   * trace_id, so this is the only correct lookup for one execution.
   */
  async getLogByTraceId(traceId: string): Promise<NormalizedAgentLogRow | null> {
    if (!traceId) throw new Error('getLogByTraceId: a trace id is required');
    const url = `${API_BASE_URL}/api/logs/by-trace/${encodeURIComponent(traceId)}`;
    const response = await authFetch(url);
    if (response.status === 404) return null;
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`GET ${url} returned HTTP ${response.status}${body ? `: ${body.slice(0, 300)}` : ''}`);
    }
    const data = await response.json();
    if (!data || !data.log) throw new Error(`GET ${url} returned no log in its body`);
    return agentApiFixed.normalizeAgentLogRow(data.log);
  },

  /**
   * Shape a row from the by-trace route into the AgentLog the viewers read. The
   * route returns either an agent_logs row (traceId/trace_id, timestamp, parsed_output,
   * raw_output, response, duration/duration_ms) or an agent_test_runs row
   * (trace_id, created_at/completed_at, result, processing_time_ms, error; measured
   * on db2 2026-09-17). Field names are mapped; nothing is invented: an absent
   * timestamp stays '', an absent output stays undefined. Idempotent: a normalised
   * row normalises to itself, so mappers may call it again without harm (pinned in
   * __tests__/log-row-readers.census.test.ts).
   */
  normalizeAgentLogRow(row: Record<string, any>): NormalizedAgentLogRow {
    const pick = (...vals: unknown[]) => vals.find((v) => v !== undefined && v !== null && v !== '');
    // The three duration columns are `integer` on db2 (information_schema, 2026-09-17), so pg
    // hands them over as numbers. Null and absence are preserved; anything else is REFUSED
    // loudly, so a producer that ever writes a string (a NUMERIC column, say) is a visible
    // defect rather than "not recorded" (#6315 round 6 ruling).
    const numericColumn = (r: Record<string, any>, col: string): number | undefined => {
      const v = r[col];
      if (v === undefined || v === null) return undefined;
      if (typeof v === 'number' && Number.isFinite(v)) return v;
      throw new Error(`normalizeAgentLogRow: ${col} is ${JSON.stringify(v)} (${typeof v}); the column is integer and only a finite number is accepted`);
    };
    const ts = pick(row.timestamp, row.completed_at, row.created_at);
    return {
      ...(row as Partial<AgentLog>),
      traceId: String(pick(row.traceId, row.trace_id) ?? ''),
      agent_type: String(pick(row.agent_type, row.agentType) ?? ''),
      timestamp: ts instanceof Date ? ts.toISOString() : ts != null ? String(ts) : '',
      // Input: the writers' columns in INPUT_SHAPE_TABLE order, skipping the '{}' column
      // default. Outputs likewise: raw_output / response (logs-postgresql.js:292 POST,
      // agents' INSERTs) / result (auto-evaluator-style INSERTs, agent_test_runs.result).
      input: firstRecorded(row, INPUT_SHAPE_TABLE.map((c) => c.path)) as AgentLog['input'],
      prompt: typeof row.prompt === 'string' ? row.prompt : '',
      raw_output: firstRecorded(row, [['raw_output'], ['response'], ['result']]),
      parsed_output: firstRecorded(row, [['parsed_output'], ['result'], ['response']]),
      // Every recorded duration column is milliseconds at its writer (DURATION_COLUMN_TABLE in
      // lib/agent-log-duration.ts anchors each one), so all three fold into duration_ms and the
      // bare `duration` key is dropped so no reader can mistake it for seconds.
      duration: undefined,
      duration_ms: pick(
        numericColumn(row, 'duration_ms'),
        numericColumn(row, 'processing_time_ms'),
        numericColumn(row, 'duration'),
      ) as number | undefined,
      status: typeof row.status === 'string' ? row.status : null,
      error: typeof row.error === 'string' ? row.error : null,
      metadata: row.metadata && typeof row.metadata === 'object' ? row.metadata : undefined,
    } as NormalizedAgentLogRow;
  },

  /**
   * Replay a specific agent log with the current or a specific prompt
   */
  async replayLog(
    logId: string,
    options: { useCurrentPrompt?: boolean; customPrompt?: string } = {}
  ): Promise<NormalizedAgentLogRow> {
    // No replay route exists in backend-merged at origin/next (2026-09-17: no
    // router handles "replay" outside OpenReplay session fields), so this request
    // fails; it now fails LOUDLY. The previous fallback returned an invented
    // page-role log with a fixed trace id and page id as if the replay had run.
    const requestBody: Record<string, unknown> = { logId };
    if (options.useCurrentPrompt) requestBody.useCurrentPrompt = true;
    if (options.customPrompt) requestBody.customPrompt = options.customPrompt;
    const url = `${API_BASE_URL}/agents/logs/replay`;
    const response = await authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`POST ${url} returned HTTP ${response.status}${body ? `: ${body.slice(0, 300)}` : ''} (no replay route exists on the backend)`);
    }
    const data = await response.json();
    const row = data && typeof data === 'object' ? (data.log ?? data) : null;
    if (!row || typeof row !== 'object') throw new Error(`POST ${url} returned no log row in its body`);
    return agentApiFixed.normalizeAgentLogRow(row);
  },

  /**
   * Run a test agent execution
   */
  async testAgentExecution(input: AgentTestInput): Promise<AgentTestResult> {
    debugLog(`Testing agent execution for ${input.agent_type}`, input);

    try {
      // Record request start time
      const requestStartTime = Date.now();

      // Ensure all required parameters are included based on our test findings
      // Use different prompt templates based on agent type
      let defaultPrompt;
      if (input.agent_type === 'customer-intent') {
        defaultPrompt = 'Analyze the following customer message to determine the intent.\n\nPage ID: {{pageId}}\nSession ID: {{sessionId}}\nCustomer Message: {{customerMessage}}';
      } else {
        defaultPrompt = 'Analyze the following page content and determine its role(s).\n\nPage ID: {{pageId}}\nSession ID: {{sessionId}}\nContent: {{pageContent}}';
      }
      
      const enhancedInput = {
        ...input,
        // Add prompt if not provided (required by the server)
        prompt: input.prompt || defaultPrompt,
        // Add test_data if not provided (required by the server)
        test_data: input.test_data || {
          pageId: input.pageId,
          sessionId: input.sessionId,
          pageContent: input.agent_type === 'customer-intent' ? '' : input.pageContent,
          customerMessage: input.agent_type === 'customer-intent' ? input.customerMessage || input.pageContent : input.customerMessage
        }
      };

      debugLog('Enhanced input with required parameters', enhancedInput);

      // Make actual API call to the backend - use consistent endpoint from constants
      const response = await authFetch(API_ENDPOINTS.AGENT_TEST, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(enhancedInput),
      });

      if (!response.ok) {
        debugLog(`Test agent execution failed with status: ${response.status}`);
        
        // Try to get more information about the error
        try {
          const errorData = await response.json();
          debugLog(`Error details:`, errorData);
          throw new Error(errorData.message || `Failed to execute agent test: ${response.status}`);
        } catch (parseError) {
          throw new Error(`Failed to execute agent test: ${response.status}`);
        }
      }

      // Calculate total request time (including network latency)
      const totalRequestTime = Date.now() - requestStartTime;

      const result = await response.json();

      // Add timing information to the log
      if (result.log) {
        result.log.total_request_time = totalRequestTime;
      }

      debugLog('Real API response for agent execution:', result);
      return result;
    } catch (error) {
      console.error('Error testing agent:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : String(error),
        log: null as any
      };
    }
  },

  /*
   * rateAgentExecution and getRatingsByTraceId were removed here in task-flqblaa1r2.
   *
   * rateAgentExecution never made an HTTP call: it slept 300-500ms via Math.random and
   * returned { success: true } with a fabricated _id, so every caller was told a rating had
   * been stored. Its own comment said "In a real implementation, this would call your
   * backend API. For now, we'll mock the response."
   *
   * getRatingsByTraceId was worse and had NO callers at all -- it returned a RANDOMLY
   * GENERATED rating 30% of the time (Math.random() > 0.7), random up/down, random
   * timestamp. Dead code returning null is inert; dead code returning plausible fabricated
   * data is armed, because whoever wires it up gets a working-looking feature with invented
   * content and nothing at the call site looks wrong.
   *
   * Measured before removal: no rating route anywhere in backend-merged/routes or
   * /services, and on db2 to_regclass is NULL for agent_ratings, agent_feedback and
   * ratings, with no %rating% table in public (positive control in the same query:
   * agent_logs returns non-null). There was nothing to wire these to.
   */
};
